/**
 * SOVRANOVA — what the auth link in the URL is telling us.
 * (Pre-beta user-journey audit, 4 Sep 2026.)
 *
 * Supabase sends people back to the app with state in the URL fragment:
 *
 *   success (recovery)  #access_token=…&type=recovery&…
 *   success (signup)    #access_token=…&type=signup&…
 *   failure (either)    #error=access_denied&error_code=otp_expired
 *                       &error_description=Email+link+is+invalid+or+has+expired
 *
 * Before this module the app read none of it. A confirmation link that had
 * expired — or that a mail scanner had already opened, which is what the audit
 * observed at 15 seconds after send — dropped the person on a plain sign-in
 * card with no message at all, and the raw error sat in the address bar.
 *
 * This is read ONCE, synchronously, at module load: before React renders, and
 * before supabase-js has finished its own async look at the URL. That ordering
 * matters twice over —
 *   • the error case must be captured before anything clears the hash, and
 *   • the recovery case must be known before the shell decides whether to show
 *     the dashboard or the onboarding flow, so a recovery link can never be
 *     mistaken for an ordinary sign-in.
 *
 * We deliberately do NOT clear a recovery hash: supabase-js still has to read
 * it to establish the session that authorises the password change. We DO clear
 * an error hash, because nothing else needs it and it should not survive a
 * refresh or get pasted into a support message.
 *
 * Nothing here is persisted, and no token value is ever read, logged or copied
 * — only the presence and the `type`.
 */

function parseParams(raw) {
  const out = {};
  const s = String(raw || '').replace(/^[#?]/, '');
  if (!s) return out;
  for (const part of s.split('&')) {
    if (!part) continue;
    const i = part.indexOf('=');
    const k = i === -1 ? part : part.slice(0, i);
    const v = i === -1 ? '' : part.slice(i + 1);
    try { out[decodeURIComponent(k)] = decodeURIComponent(v.replace(/\+/g, ' ')); }
    catch { out[k] = v; }
  }
  return out;
}

/**
 * Classify an auth redirect. Exported for tests; takes the two URL pieces so it
 * can be exercised without a browser.
 * @returns {{kind:'recovery'|'error'|'none', errorCode:string|null, linkKind:'recovery'|'signup'|'unknown'}}
 */
export function classifyAuthUrl(hash, search) {
  const p = { ...parseParams(search), ...parseParams(hash) };

  if (p.error || p.error_code || p.error_description) {
    // `type` is absent on most Supabase error redirects, so we cannot always
    // tell a dead signup link from a dead recovery link. The UI copy is written
    // to be true either way and offers both routes forward.
    const linkKind = p.type === 'recovery' ? 'recovery' : p.type === 'signup' ? 'signup' : 'unknown';
    return { kind: 'error', errorCode: String(p.error_code || p.error || 'invalid_link'), linkKind };
  }

  if (p.type === 'recovery') return { kind: 'recovery', errorCode: null, linkKind: 'recovery' };

  return { kind: 'none', errorCode: null, linkKind: 'unknown' };
}

/**
 * Is this an expired / already-used / otherwise dead link, as opposed to some
 * other auth error? These are the ones worth offering a resend for.
 */
export function isDeadLinkError(errorCode) {
  return /otp_expired|access_denied|invalid|expired/i.test(String(errorCode || ''));
}

const initial = (typeof window !== 'undefined')
  ? classifyAuthUrl(window.location.hash, window.location.search)
  : { kind: 'none', errorCode: null, linkKind: 'unknown' };

// Strip a dead-link hash from the address bar immediately. Safe: no session can
// be established from it, so supabase-js has nothing to lose by its absence.
if (typeof window !== 'undefined' && initial.kind === 'error') {
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } catch { /* a failed tidy-up must never break sign-in */ }
}

/** The auth state this page load arrived with. Constant for the page's life. */
export function getInitialAuthUrlState() { return initial; }

/** True when this page load came from a password-reset link. */
export function arrivedFromRecoveryLink() { return initial.kind === 'recovery'; }
